/**
 * Payment terms — shared, normalized value object used across the ERP cluster
 * (accounting, sales, procurement). Payment terms are NEVER stored as free
 * text: a term is (basis, net_days) plus an optional reference to a
 * tenant-configured preset.
 *
 * The API entity types are the orval-generated ones (JSON-schema first) —
 * re-exported here so consumers keep importing from this module.
 */
import { PaymentTermsBasis } from '@/generated/api/models/paymentTermsBasis';

export type { PaymentTermCreate } from '@/generated/api/models/paymentTermCreate';
export type { PaymentTermUpdate } from '@/generated/api/models/paymentTermUpdate';

// The due-date basis. The generated model IS this union
// (payment_terms_basis.schema.json), re-exported so consumers keep importing it here.
export type { PaymentTermsBasis } from '@/generated/api/models/paymentTermsBasis';

/**
 * Every basis, in picker order — derived from the generated const rather than
 * re-typed, so a value added to the schema reaches the pickers without a second
 * hand-written copy having to be remembered.
 */
export const PAYMENT_TERMS_BASES: PaymentTermsBasis[] = Object.values(PaymentTermsBasis);

/**
 * A tenant-configured payment-terms preset (accounting_payment_terms row).
 * `basis` is now generated as the exact `net | eom | due_on_receipt` union
 * (payment_term.schema.json constrains it with an `enum`), so the generated
 * type is used directly — no narrowing wrapper needed.
 */
export type { PaymentTerm } from '@/generated/api/models/paymentTerm';

/**
 * The normalized value a document/account stores for its payment terms.
 * `payment_terms_id` references a preset (null = ad-hoc override); `basis` and
 * `days` are always the source of truth for due-date computation.
 */
export interface PaymentTermsValue {
  payment_terms_id: string | null;
  payment_terms_basis: PaymentTermsBasis | null;
  payment_terms_days: number | null;
}
